import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Modal, Spinner, Row, Col, Input,
  Nav, NavItem, NavLink, TabContent, TabPane, Card, CardBody, Badge,
} from 'reactstrap';
import { motion } from 'framer-motion';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import {
  fetchDesc,
  fetchMaterioActivity,
  createDesc,
  updateDesc,
  changeStatusDesc,
  addDescAttachment,
  removeDescAttachment,
  clearMaterioMessages,
} from '../../store/materiovigilance/actions';
import { formatCreatedDate } from '../../constants/utils';
import { uploadServiceContractFiles, validateFiles, formatFileSize } from '../../helpers/fileUpload';
import usePermission from '../../Hooks/usePermission';
import {
  MATERIOVIGILANCE_UPDATE,
  MATERIOVIGILANCE_CLOSE,
  MATERIOVIGILANCE_REOPEN,
} from '../../constants/permissions';
import AssetMultiPicker from './AssetMultiPicker';
import WorkOrderMultiPicker from './WorkOrderMultiPicker';
import RecipientUserMultiPicker from './RecipientUserMultiPicker';
import MaterioStatusBadge from './MaterioStatusBadge';
import materioMultiSelectStyles from './materioSelectStyles';
import AssetListTable from '../../components/Common/Table/AssetListTable';
import AssetModal from '../../components/AssetModal';
import TicketDetailsModal from '../../components/TicketDetailsModal';
import api from '../../helpers/api';

const DOC_TYPES = ['NATIONAL_AGENCY_ALERT', 'MANUFACTURER_LETTER', 'INTERNAL_NOTE', 'SIGNAL_REPORT', 'OTHER'];
const SEVERITIES = ['INCIDENT', 'NEAR_MISS', 'VIGILANCE_SIGNAL'];
const ACTIONS = [
  'IMMEDIATE_WITHDRAWAL',
  'QUARANTINE',
  'STOCK_VERIFICATION',
  'ENHANCED_MONITORING',
  'RETURN_TO_MANUFACTURER',
  'APPLY_MANUFACTURER_RECOMMENDATIONS',
  'OTHER',
];
const ORIGINS = ['NATIONAL_AGENCY', 'MANUFACTURER', 'INTERNAL'];
const STATUSES = ['DRAFT', 'DECLARED', 'UNDER_INVESTIGATION', 'CLOSED'];

// Status options for the Management tab grid — icon + label + sub-description.
// Mirrors `reactiveStatusOptions(t)` shape used in TicketDetailsModal.
const STATUS_OPTIONS = (t) => [
  {
    value: 'DRAFT',
    icon: 'edit-3',
    label: t('compliance.materiovigilance.status.draft', 'Draft'),
    description: t('compliance.materiovigilance.status_descriptions.draft', 'Initial draft, not yet declared'),
  },
  {
    value: 'DECLARED',
    icon: 'send',
    label: t('compliance.materiovigilance.status.declared', 'Declared'),
    description: t('compliance.materiovigilance.status_descriptions.declared', 'Officially declared'),
  },
  {
    value: 'UNDER_INVESTIGATION',
    icon: 'search',
    label: t('compliance.materiovigilance.status.under_investigation', 'Under investigation'),
    description: t('compliance.materiovigilance.status_descriptions.under_investigation', 'Investigation in progress'),
  },
  {
    value: 'CLOSED',
    icon: 'check-circle',
    label: t('compliance.materiovigilance.status.closed', 'Closed'),
    description: t('compliance.materiovigilance.status_descriptions.closed', 'Investigation closed'),
  },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

// Card animation variants — same as WorkOrderDetailsModal cardVariants.
const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const emptyForm = () => ({
  id: null,
  alertNumber: '',
  incidentDate: todayIso(),
  status: 'DRAFT',
  origin: 'NATIONAL_AGENCY',
  documentTypes: [],
  documentTypeOther: '',
  description: '',
  severity: 'INCIDENT',
  actions: [],
  actionsOther: '',
  linkedAssetIds: [],
  recipientUserIds: [],
  linkedWorkOrderIds: [],
});

const MaterioDescendingModal = ({ isOpen, toggle, mode: initialMode, recordId = null, onChanged }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();
  const { currentDesc, currentDescLoading, saving, saveError, activity } = useSelector((s) => s.Materiovigilance);
  const authUser = JSON.parse(localStorage.getItem('authUser') || '{}');
  const orgId = authUser?.activeOrganizationId ?? null;

  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(emptyForm());
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [validationError, setValidationError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const fileInputRef = useRef(null);
  const [assetModal, setAssetModal] = useState({ isOpen: false, userData: null, loading: false });
  const [ticketModal, setTicketModal] = useState({ isOpen: false, ticketNumber: null, loading: false });

  const handleAssetClick = async (asset) => {
    if (!asset?.id) return;
    setAssetModal({ isOpen: true, userData: asset, loading: true });
    try {
      const { data } = await api.get(`asset/get-by-id?id=${asset.id}`);
      const full = data?.response || asset;
      setAssetModal({ isOpen: true, userData: full, loading: false });
    } catch (e) {
      setAssetModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleTicketClick = (ticketNumber) => {
    if (!ticketNumber) return;
    setTicketModal({ isOpen: true, ticketNumber, loading: false });
  };

  useEffect(() => {
    if (!isOpen) return;
    setMode(initialMode);
    setValidationError(null);
    setUploadError(null);
    setPendingAttachments([]);
    setActiveTab('details');
    if (initialMode === 'create') {
      setForm(emptyForm());
    } else if (recordId) {
      dispatch(fetchDesc(recordId));
      dispatch(fetchMaterioActivity('DESCENDING', recordId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialMode, recordId]);

  useEffect(() => {
    if (!isOpen || initialMode === 'create' || !currentDesc) return;
    setForm({
      id: currentDesc.id,
      alertNumber: currentDesc.alertNumber || '',
      incidentDate: currentDesc.incidentDate || todayIso(),
      status: currentDesc.status || 'DRAFT',
      origin: currentDesc.origin || 'NATIONAL_AGENCY',
      documentTypes: currentDesc.documentTypes ? Array.from(currentDesc.documentTypes) : [],
      documentTypeOther: currentDesc.documentTypeOther || '',
      description: currentDesc.description || '',
      severity: currentDesc.severity || 'INCIDENT',
      actions: currentDesc.actions ? Array.from(currentDesc.actions) : [],
      actionsOther: currentDesc.actionsOther || '',
      linkedAssetIds: currentDesc.linkedAssetIds ? Array.from(currentDesc.linkedAssetIds) : [],
      recipientUserIds: currentDesc.recipientUserIds ? Array.from(currentDesc.recipientUserIds) : [],
      linkedWorkOrderIds: currentDesc.linkedWorkOrderIds ? Array.from(currentDesc.linkedWorkOrderIds) : [],
    });
  }, [currentDesc, isOpen, initialMode]);

  useEffect(() => () => dispatch(clearMaterioMessages()), [dispatch]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ----- option builders for react-select --------------------------------
  const originOptions = useMemo(() => ORIGINS.map((o) => ({
    value: o,
    label: t(`compliance.materiovigilance.descending.origin.${o.toLowerCase()}`, o),
  })), [t]);
  const docTypeOptions = useMemo(() => DOC_TYPES.map((dt) => ({
    value: dt,
    label: t(`compliance.materiovigilance.descending.document_type.${dt.toLowerCase()}`, dt),
  })), [t]);
  const severityOptions = useMemo(() => SEVERITIES.map((s) => ({
    value: s,
    label: t(`compliance.materiovigilance.descending.severity.${s.toLowerCase()}`, s),
  })), [t]);
  const actionOptions = useMemo(() => ACTIONS.map((a) => ({
    value: a,
    label: t(`compliance.materiovigilance.descending.action.${a.toLowerCase()}`, a),
  })), [t]);
  const statusOptions = useMemo(() => STATUSES.map((s) => ({
    value: s,
    label: t(`compliance.materiovigilance.status.${s.toLowerCase()}`, s),
  })), [t]);

  const validate = () => {
    if (!form.alertNumber.trim()) return t('validation.materiovigilance.alert_number.required', 'Alert number is required');
    if (!form.incidentDate) return t('validation.materiovigilance.incident_date.required', 'Incident date is required');
    if (!form.origin) return t('validation.materiovigilance.origin.required', 'Origin is required');
    if (!form.documentTypes.length) return t('validation.materiovigilance.document_types.required', 'Select at least one document type');
    if (form.documentTypes.includes('OTHER') && !form.documentTypeOther.trim()) {
      return t('validation.materiovigilance.document_type_other.required', 'Specify the other document type');
    }
    if (!form.description.trim()) return t('validation.materiovigilance.description.required', 'Description is required');
    if (!form.severity) return t('validation.materiovigilance.severity.required', 'Severity is required');
    if (!form.actions.length) return t('validation.materiovigilance.actions.required', 'Select at least one action');
    if (form.actions.includes('OTHER') && !form.actionsOther.trim()) {
      return t('validation.materiovigilance.actions_other.required', 'Specify the other action');
    }
    if (!form.linkedAssetIds.length) return t('validation.materiovigilance.linked_assets.required', 'Link at least one asset');
    return null;
  };

  const handleFiles = async (e) => {
    setUploadError(null);
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    if (!files.length) return;
    const { valid, errors, validFiles } = validateFiles(files);
    if (!valid) {
      setUploadError(errors.join('\n'));
      if (!validFiles.length) return;
    }
    const urls = await uploadServiceContractFiles(validFiles);
    const items = validFiles.map((f, i) => ({
      label: f.name,
      fileUrl: urls[i],
      fileName: f.name,
      contentType: f.type,
      fileSize: f.size,
    })).filter((i) => i.fileUrl);
    if (mode === 'edit' && form.id) {
      items.forEach((it) => dispatch(addDescAttachment({ materioDescendingId: form.id, ...it })));
    } else {
      setPendingAttachments((cur) => [...cur, ...items]);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const err = validate();
    if (err) { setValidationError(err); return; }
    setValidationError(null);
    const payload = { ...form };
    if (mode === 'edit' && form.id) {
      dispatch(updateDesc(payload, () => { if (onChanged) onChanged(); toggle(); }));
    } else {
      dispatch(createDesc(payload, (item) => {
        if (item?.id && pendingAttachments.length) {
          pendingAttachments.forEach((att) => dispatch(addDescAttachment({ materioDescendingId: item.id, ...att })));
        }
        if (onChanged) onChanged();
        toggle();
      }));
    }
  };

  const handleStatusSelect = (toStatus) => {
    if (!toStatus || !form.id || toStatus === currentDesc?.status) return;
    dispatch(changeStatusDesc({ id: form.id, toStatus }, () => {
      dispatch(fetchDesc(form.id));
      dispatch(fetchMaterioActivity('DESCENDING', form.id));
      if (onChanged) onChanged();
    }));
  };

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isCreate = mode === 'create';

  // Per-target permission gate (mirrors backend MaterioStateMachine).
  const canSelectStatus = (target) => {
    if (target === currentDesc?.status) return true;       // current is "selected" but not actionable
    if (target === 'CLOSED') return hasPermission(MATERIOVIGILANCE_CLOSE);
    if (currentDesc?.status === 'CLOSED') return hasPermission(MATERIOVIGILANCE_REOPEN);
    return hasPermission(MATERIOVIGILANCE_UPDATE);
  };

  const titleText = isCreate
    ? t('compliance.materiovigilance.descending.new', 'New descending materiovigilance')
    : isEdit
      ? t('compliance.materiovigilance.descending.edit', 'Edit descending materiovigilance')
      : (currentDesc?.alertNumber || t('compliance.materiovigilance.descending.view', 'Materiovigilance — descending'));

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size={isView ? 'xl' : 'lg'}
      centered
      className="modern-modal"
      backdrop="static"
      style={{ maxHeight: '90vh' }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="modal-content border-0 shadow-lg"
        style={{ borderRadius: '12px', overflow: 'hidden' }}
      >
        {isView ? (
          <div
            className="modal-header border-0 text-white position-sticky d-flex justify-content-between align-items-center"
            style={{
              top: 0,
              zIndex: 10,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              padding: '1.5rem',
            }}
          >
            <div className="d-flex align-items-center">
              <div className="me-3">
                <div
                  className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 48, height: 48 }}
                >
                  <FeatherIcon icon="alert-triangle" size={24} className="text-white" />
                </div>
              </div>
              <div className="flex-grow-1">
                <h4 className="modal-title mb-1 text-white d-flex align-items-center">
                  <span className="me-2">{currentDesc?.alertNumber || titleText}</span>
                  {currentDesc?.status && <MaterioStatusBadge status={currentDesc.status} />}
                </h4>
                <div className="d-flex align-items-center gap-3">
                  {currentDesc?.incidentDate && (
                    <span className="text-white small">
                      <FeatherIcon icon="calendar" size={14} className="me-1" />
                      {formatCreatedDate(currentDesc.incidentDate)}
                    </span>
                  )}
                  {currentDesc?.issuerUserName && (
                    <span className="text-white small">
                      <FeatherIcon icon="user" size={14} className="me-1" />
                      {currentDesc.issuerUserName}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 ms-auto">
              {hasPermission(MATERIOVIGILANCE_UPDATE) && (
                <button
                  type="button"
                  className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                  onClick={() => setMode('edit')}
                  title={t('shared.edit', 'Edit')}
                  style={{
                    background: 'rgba(255,255,255,0.95)',
                    color: '#495057',
                    fontWeight: 500,
                    borderRadius: 8,
                  }}
                >
                  <FeatherIcon icon="edit-3" size={16} />
                  <span>{t('shared.edit', 'Edit')}</span>
                </button>
              )}
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={toggle}
                aria-label={t('shared.close', 'Close')}
              />
            </div>
          </div>
        ) : (
          <div className="modal-header">
            <div className="d-flex align-items-center gap-2">
              <h5 className="modal-title fs-4 mb-0">{titleText}</h5>
            </div>
            <button type="button" className="btn-close" onClick={toggle} aria-label={t('shared.close', 'Close')} />
          </div>
        )}

        <div className={isView ? 'modal-body p-0' : 'modal-body p-4'}
             style={{ maxHeight: isView ? 'calc(90vh - 220px)' : 'calc(90vh - 140px)', overflow: 'auto' }}>
          {validationError && <div className="alert alert-warning m-3 mb-0">{validationError}</div>}
          {saveError && <div className="alert alert-danger m-3 mb-0">{String(saveError)}</div>}
          {uploadError && <div className="alert alert-danger m-3 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{uploadError}</div>}

          {currentDescLoading && !isCreate ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : isView ? (
            <DescendingViewTabs
              record={currentDesc}
              activity={activity}
              t={t}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              canSelectStatus={canSelectStatus}
              onStatusSelect={handleStatusSelect}
              onAssetClick={handleAssetClick}
              onTicketClick={handleTicketClick}
            />
          ) : (
            <DescendingFormBody
              form={form}
              set={set}
              orgId={orgId}
              t={t}
              mode={mode}
              originOptions={originOptions}
              docTypeOptions={docTypeOptions}
              severityOptions={severityOptions}
              actionOptions={actionOptions}
              statusOptions={statusOptions}
              existingAttachments={currentDesc?.attachments || []}
              pendingAttachments={pendingAttachments}
              onAttachClick={() => fileInputRef.current?.click()}
              fileInputRef={fileInputRef}
              onFiles={handleFiles}
              onRemovePending={(idx) => setPendingAttachments((cur) => cur.filter((_, i) => i !== idx))}
              onRemoveExisting={(attId) => dispatch(removeDescAttachment(attId, form.id))}
            />
          )}
        </div>

        {!isView && (
          <div className="modal-footer bg-light border-top-0">
            <button type="button" className="btn btn-outline-secondary" onClick={toggle}>{t('shared.cancel', 'Cancel')}</button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? <><Spinner size="sm" className="me-2" />{t('shared.saving', 'Saving…')}</>
                : isEdit ? t('shared.update', 'Update') : t('shared.create', 'Create')}
            </button>
          </div>
        )}
      </motion.div>
      <AssetModal
        isOpen={assetModal.isOpen}
        toggle={() => setAssetModal((prev) => ({ ...prev, isOpen: false }))}
        userData={assetModal.userData}
        loading={assetModal.loading}
        formatCreatedDate={formatCreatedDate}
        onTicketClick={handleTicketClick}
      />
      <TicketDetailsModal
        isOpen={ticketModal.isOpen}
        toggle={() => setTicketModal((prev) => ({ ...prev, isOpen: false }))}
        ticketNumber={ticketModal.ticketNumber}
        loading={ticketModal.loading}
        formatCreatedDate={formatCreatedDate}
      />
    </Modal>
  );
};

// ----- form body (single column, all dropdowns) -----------------------------

const DescendingFormBody = ({
  form, set, orgId, t, mode,
  originOptions, docTypeOptions, severityOptions, actionOptions, statusOptions,
  existingAttachments, pendingAttachments,
  onAttachClick, fileInputRef, onFiles, onRemovePending, onRemoveExisting,
}) => (
  <>
    <FieldRow label={t('compliance.materiovigilance.descending.field.alert_number', 'Alert number')} required>
      <Input type="text" value={form.alertNumber} onChange={(e) => set('alertNumber', e.target.value)} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.descending.field.incident_date', 'Detection date')} required>
      <Input type="date" value={form.incidentDate} onChange={(e) => set('incidentDate', e.target.value)} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.descending.field.origin', 'Alert origin')} required>
      <Select
        options={originOptions}
        value={originOptions.find((o) => o.value === form.origin) || null}
        onChange={(opt) => set('origin', opt ? opt.value : null)}
        classNamePrefix="react-select"
        placeholder={t('compliance.materiovigilance.placeholder.select', 'Select…')}
      />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.descending.field.document_types', 'Document types')} required>
      <Select
        isMulti
        options={docTypeOptions}
        value={docTypeOptions.filter((o) => form.documentTypes.includes(o.value))}
        onChange={(opts) => set('documentTypes', (opts || []).map((o) => o.value))}
        classNamePrefix="react-select"
        styles={materioMultiSelectStyles}
        placeholder={t('compliance.materiovigilance.placeholder.select_multi', 'Select one or more…')}
      />
    </FieldRow>
    {form.documentTypes.includes('OTHER') && (
      <FieldRow label={t('compliance.materiovigilance.descending.field.document_type_other', 'Specify other document type')} required>
        <Input type="text" value={form.documentTypeOther} onChange={(e) => set('documentTypeOther', e.target.value)} />
      </FieldRow>
    )}
    <FieldRow label={t('compliance.materiovigilance.descending.field.description', 'Description')} required>
      <Input type="textarea" rows={5} value={form.description} onChange={(e) => set('description', e.target.value)} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.descending.field.severity', 'Severity')} required>
      <Select
        options={severityOptions}
        value={severityOptions.find((o) => o.value === form.severity) || null}
        onChange={(opt) => set('severity', opt ? opt.value : null)}
        classNamePrefix="react-select"
        placeholder={t('compliance.materiovigilance.placeholder.select', 'Select…')}
      />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.descending.field.attachments', 'Files')}>
      <div
        className="border rounded p-4 text-center"
        style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#dee2e6', backgroundColor: '#fafbfc' }}
      >
        <input
          ref={fileInputRef}
          type="file"
          id="materioFileUploadInput"
          className="d-none"
          multiple
          onChange={onFiles}
        />
        <label
          htmlFor="materioFileUploadInput"
          className="btn btn-outline-primary mb-2"
          style={{ cursor: 'pointer' }}
        >
          <FeatherIcon icon="upload" size={16} className="me-2" />
          {t('shared.upload_files', 'Upload Files')}
        </label>
        <p className="text-muted small mb-0">{t('shared.click_to_upload', 'Click to select files to upload')}</p>
      </div>
      {((mode === 'edit' && existingAttachments.length) || pendingAttachments.length) ? (
        <ul className="list-unstyled mt-2">
          {(mode === 'edit' ? existingAttachments : pendingAttachments).map((a, idx) => (
            <li key={a.id || idx} className="d-flex align-items-center justify-content-between p-2 border rounded mt-1">
              <span>
                <strong>{a.label || a.fileName}</strong>
                {a.fileSize ? <small className="text-muted ms-2">{formatFileSize(a.fileSize)}</small> : null}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => mode === 'edit' ? onRemoveExisting(a.id) : onRemovePending(idx)}
              >×</button>
            </li>
          ))}
        </ul>
      ) : null}
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.descending.field.linked_assets', 'Assets')} required>
      <AssetMultiPicker organizationId={orgId} value={form.linkedAssetIds} onChange={(v) => set('linkedAssetIds', v)} />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.descending.field.actions', 'Actions')} required>
      <Select
        isMulti
        options={actionOptions}
        value={actionOptions.filter((o) => form.actions.includes(o.value))}
        onChange={(opts) => set('actions', (opts || []).map((o) => o.value))}
        classNamePrefix="react-select"
        styles={materioMultiSelectStyles}
        placeholder={t('compliance.materiovigilance.placeholder.select_multi', 'Select one or more…')}
      />
    </FieldRow>
    {form.actions.includes('OTHER') && (
      <FieldRow label={t('compliance.materiovigilance.descending.field.actions_other', 'Specify other action')} required>
        <Input type="text" value={form.actionsOther} onChange={(e) => set('actionsOther', e.target.value)} />
      </FieldRow>
    )}
    <FieldRow label={t('compliance.materiovigilance.descending.field.recipients', 'Recipients')}>
      <RecipientUserMultiPicker
        organizationId={orgId}
        value={form.recipientUserIds}
        onChange={(v) => set('recipientUserIds', v)}
      />
    </FieldRow>
    <FieldRow label={t('compliance.materiovigilance.descending.field.linked_work_orders', 'Work Orders')}>
      <WorkOrderMultiPicker organizationId={orgId} value={form.linkedWorkOrderIds} onChange={(v) => set('linkedWorkOrderIds', v)} />
    </FieldRow>
    {mode === 'create' && (
      <FieldRow label={t('compliance.materiovigilance.descending.field.initial_status', 'Initial status')} required>
        <Select
          options={statusOptions}
          value={statusOptions.find((o) => o.value === form.status) || null}
          onChange={(opt) => set('status', opt ? opt.value : 'DRAFT')}
          classNamePrefix="react-select"
          placeholder={t('compliance.materiovigilance.placeholder.select', 'Select…')}
        />
      </FieldRow>
    )}
  </>
);

// ----- read-only tabbed view (mirrors WorkOrderDetailsModal patterns) -----

const TAB_DEFS = (t) => [
  { key: 'details',     label: t('shared.details', 'Details'),                                  icon: 'file-text' },
  { key: 'management',  label: t('compliance.materiovigilance.tab.management', 'Management'),   icon: 'sliders' },
  { key: 'assets',      label: t('compliance.materiovigilance.tab.assets', 'Assets'),           icon: 'package' },
  { key: 'workorders',  label: t('compliance.materiovigilance.tab.work_orders', 'Work Orders'), icon: 'briefcase' },
  { key: 'attachments', label: t('compliance.materiovigilance.tab.attachments', 'Attachments'), icon: 'paperclip' },
  { key: 'activity',    label: t('shared.activity', 'Activity'),                                icon: 'activity' },
];

// --- shared sub-components used inside DescendingViewTabs ---

// Section header inside a Card — icon-circle + title + subtitle.
// Identical DOM to WorkOrderDetailsModal renderBasicInfoTab section headers.
const SectionHeader = ({ icon, color = 'primary', title, subtitle, right }) => (
  <div className="d-flex align-items-center justify-content-between mb-3">
    <div className="d-flex align-items-center">
      <div className="info-icon me-3">
        <div
          className={`bg-${color} bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center`}
          style={{ width: '40px', height: '40px' }}
        >
          <FeatherIcon icon={icon} size={20} className={`text-${color}`} />
        </div>
      </div>
      <div>
        <h6 className="mb-1 fw-bold">{title}</h6>
        {subtitle && <p className="text-muted small mb-0">{subtitle}</p>}
      </div>
    </div>
    {right}
  </div>
);

// Single info-item field row inside info-grid — exact platform pattern.
const InfoItem = ({ icon, label, last, children }) => (
  <div className={`info-item ${last ? 'mb-0' : 'mb-3'}`}>
    <div className="d-flex align-items-center mb-1">
      <FeatherIcon icon={icon} size={14} className="text-muted me-2" />
      <span className="fw-semibold text-muted small">{label}</span>
    </div>
    {typeof children === 'string' || typeof children === 'number'
      ? <p className="mb-0 text-dark">{children || '—'}</p>
      : (children ?? <p className="mb-0 text-dark">—</p>)}
  </div>
);

// Empty-state — same 64×64 muted circle pattern used everywhere in WO modal.
const EmptyState = ({ icon, title, description }) => (
  <div className="text-center py-5">
    <div
      className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
      style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}
    >
      <FeatherIcon icon={icon} size={24} className="text-muted" />
    </div>
    <h6 className="text-muted">{title}</h6>
    {description && <p className="text-muted mb-0">{description}</p>}
  </div>
);

// Friendly status label for linked work orders — same mapping used elsewhere.
const woStatusLabel = (raw, t) => {
  switch (String(raw || '').toUpperCase()) {
    case 'SUBMITTED': return t('shared.open', 'Open');
    case 'IN_PROGRESS': return t('shared.in_progress', 'In Progress');
    case 'RESOLVED': return t('shared.resolved', 'Resolved');
    case 'ON_HOLD': return t('shared.on_hold', 'On Hold');
    case 'PENDING': return t('shared.pending', 'Pending');
    default: return raw || '—';
  }
};
const woStatusColor = (raw) => {
  switch (String(raw || '').toUpperCase()) {
    case 'RESOLVED': return 'success';
    case 'IN_PROGRESS': return 'warning';
    case 'SUBMITTED': return 'info';
    case 'ON_HOLD': return 'danger';
    case 'PENDING': return 'primary';
    default: return 'secondary';
  }
};

// Activity payload renderer — keeps the same key/payload contract as
// MaterioActivityFeed, but inlined here so the timeline visual matches WO.
const renderActivityPayload = (entry, t, lookup) => {
  if (entry.kind === 'STATUS_CHANGE') {
    return (
      <div className="d-flex align-items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
        {entry.fromStatus && <MaterioStatusBadge status={entry.fromStatus} />}
        {entry.fromStatus && <FeatherIcon icon="arrow-right" size={14} className="text-muted" />}
        <MaterioStatusBadge status={entry.toStatus} />
        {entry.note && <small className="text-muted ms-2">{entry.note}</small>}
      </div>
    );
  }
  let payload = null;
  if (entry.activityData) {
    try {
      payload = typeof entry.activityData === 'string' ? JSON.parse(entry.activityData) : entry.activityData;
    } catch (e) { /* ignore */ }
  }
  if (payload?.type === 'KEYED_MESSAGE') {
    return <span>{t(payload.key, payload.key)}</span>;
  }
  if (payload?.type === 'FIELD_CHANGES') {
    return (
      <div>
        <strong>{t('activity.materio.field_changes', 'Fields updated')}:</strong>
        <ul className="mb-0 mt-1 ps-3">
          {(payload.changes || []).map((c, i) => (
            <li key={i}>
              <code>{c.field}</code>: <em>{String(c.from ?? '—')}</em> → <strong>{String(c.to ?? '—')}</strong>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return <span>{entry.activity || '—'}</span>;
};

const DescendingViewTabs = ({
  record, activity, t,
  activeTab, setActiveTab,
  canSelectStatus, onStatusSelect,
  onAssetClick, onTicketClick,
}) => {
  if (!record) return null;
  const lookup = (key, base, fallback) =>
    t(`compliance.materiovigilance.descending.${base}.${(key || '').toLowerCase()}`, fallback ?? key ?? '—');
  const tabs = TAB_DEFS(t);
  const counts = {
    assets: (record.linkedAssets || []).length || (record.linkedAssetIds || []).length || 0,
    workorders: (record.linkedWorkOrders || []).length || (record.linkedWorkOrderIds || []).length || 0,
    attachments: (record.attachments || []).length,
    activity: (activity || []).length,
  };

  return (
    <>
      <Nav tabs className="nav-tabs-custom mb-0 px-4">
        {tabs.map((tab) => (
          <NavItem key={tab.key}>
            <NavLink
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => setActiveTab(tab.key)}
              style={{ cursor: 'pointer' }}
            >
              <FeatherIcon icon={tab.icon} size={16} className="me-2" />
              {tab.label}
              {counts[tab.key] > 0 && (
                <Badge color="primary" pill className="ms-2">{counts[tab.key]}</Badge>
              )}
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <TabContent activeTab={activeTab} className="h-100">

        {/* ============== DETAILS ============== */}
        <TabPane tabId="details" className="h-100">
          <div className="p-4">
            <Row className="g-4 mb-4">
              {/* Basic Information */}
              <Col md={6}>
                <motion.div variants={cardVariants} initial="hidden" animate="visible" className="h-100">
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="info"
                        color="primary"
                        title={t('shared.basic_information', 'Basic Information')}
                        subtitle={t('compliance.materiovigilance.section.alert_subtitle', 'Core alert details')}
                      />
                      <div className="info-grid">
                        <InfoItem icon="hash" label={t('compliance.materiovigilance.descending.field.alert_number', 'Alert Number')}>
                          <p className="mb-0 text-dark fw-semibold">{record.alertNumber || '—'}</p>
                        </InfoItem>
                        <InfoItem icon="calendar" label={t('compliance.materiovigilance.descending.field.incident_date', 'Detection Date')}>
                          {record.incidentDate ? formatCreatedDate(record.incidentDate) : '—'}
                        </InfoItem>
                        <InfoItem icon="user" label={t('compliance.materiovigilance.descending.field.issuer_user', 'Created By')}>
                          {record.issuerUserName || '—'}
                        </InfoItem>
                        <InfoItem icon="globe" label={t('compliance.materiovigilance.descending.field.origin', 'Alert Origin')} last>
                          <Badge color="info" style={{ fontSize: '12px', padding: '6px 12px' }}>
                            {lookup(record.origin, 'origin')}
                          </Badge>
                        </InfoItem>
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>

              {/* Classification */}
              <Col md={6}>
                <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }} className="h-100">
                  <Card className="border-0 shadow-sm h-100">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="alert-triangle"
                        color="warning"
                        title={t('compliance.materiovigilance.section.classification', 'Classification')}
                        subtitle={t('compliance.materiovigilance.section.classification_subtitle', 'Severity, document types and actions')}
                      />
                      <div className="info-grid">
                        <InfoItem icon="alert-octagon" label={t('compliance.materiovigilance.descending.field.severity', 'Severity')}>
                          <Badge color="danger" style={{ fontSize: '12px', padding: '6px 12px' }}>
                            {lookup(record.severity, 'severity')}
                          </Badge>
                        </InfoItem>
                        <InfoItem icon="file" label={t('compliance.materiovigilance.descending.field.document_types', 'Document Types')}>
                          {(record.documentTypes && Array.from(record.documentTypes).length) ? (
                            <div className="d-flex flex-wrap gap-1">
                              {Array.from(record.documentTypes).map((dt) => (
                                <Badge key={dt} color="light" className="text-dark border" style={{ fontSize: '11px', padding: '4px 8px' }}>
                                  {lookup(dt, 'document_type')}
                                </Badge>
                              ))}
                            </div>
                          ) : <p className="mb-0 text-dark">—</p>}
                        </InfoItem>
                        {record.documentTypeOther && (
                          <InfoItem icon="edit-3" label={t('compliance.materiovigilance.descending.field.document_type_other', 'Other Document Type')}>
                            {record.documentTypeOther}
                          </InfoItem>
                        )}
                        <InfoItem icon="check-square" label={t('compliance.materiovigilance.descending.field.actions', 'Actions')} last={!record.actionsOther}>
                          {(record.actions && Array.from(record.actions).length) ? (
                            <div className="d-flex flex-wrap gap-1">
                              {Array.from(record.actions).map((a) => (
                                <Badge key={a} color="light" className="text-dark border" style={{ fontSize: '11px', padding: '4px 8px' }}>
                                  {lookup(a, 'action')}
                                </Badge>
                              ))}
                            </div>
                          ) : <p className="mb-0 text-dark">—</p>}
                        </InfoItem>
                        {record.actionsOther && (
                          <InfoItem icon="edit-3" label={t('compliance.materiovigilance.descending.field.actions_other', 'Other Action')} last>
                            {record.actionsOther}
                          </InfoItem>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            </Row>

            <Row className="g-4">
              <Col md={12}>
                <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="file-text"
                        color="secondary"
                        title={t('compliance.materiovigilance.descending.field.description', 'Description')}
                        subtitle={t('compliance.materiovigilance.section.description_subtitle', 'Full alert description')}
                      />
                      <div className="bg-light rounded p-3">
                        <p className="mb-0 text-dark" style={{ whiteSpace: 'pre-wrap' }}>
                          {record.description || t('shared.no_description_provided', 'No description provided')}
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </div>
        </TabPane>

        {/* ============== MANAGEMENT ============== */}
        <TabPane tabId="management" className="h-100">
          <div className="p-4">
            {/* Status Management — mirrors TicketDetailsModal status-buttons-grid */}
            <motion.div variants={cardVariants} initial="hidden" animate="visible">
              <Card className="border-0 shadow-sm mb-4">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-4">
                    <div className="info-icon me-3">
                      <div
                        className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '40px', height: '40px' }}
                      >
                        <FeatherIcon icon="settings" size={20} className="text-primary" />
                      </div>
                    </div>
                    <div>
                      <h5 className="mb-1 fw-bold">
                        {t('compliance.materiovigilance.section.status_management', 'Status Management')}
                      </h5>
                      <p className="text-muted small mb-0">
                        {t('compliance.materiovigilance.section.status_management_sub', 'Update alert status and workflow')}
                      </p>
                    </div>
                  </div>

                  <div className="status-buttons-grid">
                    <Row className="g-3">
                      {STATUS_OPTIONS(t).map((item) => {
                        const isActive = record.status === item.value;
                        const enabled = canSelectStatus(item.value);
                        return (
                          <Col md={6} lg={4} key={item.value}>
                            <motion.button
                              type="button"
                              whileHover={enabled && !isActive ? { scale: 1.02 } : undefined}
                              whileTap={enabled && !isActive ? { scale: 0.98 } : undefined}
                              disabled={!enabled || isActive}
                              className={`status-option-btn w-100 d-flex align-items-center p-3 rounded-3 border ${
                                isActive
                                  ? 'border-primary bg-primary text-white shadow-sm'
                                  : 'border-light bg-white text-dark hover-shadow'
                              }`}
                              style={{
                                borderWidth: '2px',
                                cursor: enabled && !isActive ? 'pointer' : 'not-allowed',
                                opacity: !enabled && !isActive ? 0.5 : 1,
                                transition: 'all 0.3s ease',
                              }}
                              onClick={() => onStatusSelect(item.value)}
                            >
                              <div
                                className={`status-icon me-3 rounded-circle d-flex align-items-center justify-content-center ${
                                  isActive ? 'bg-white bg-opacity-10' : 'bg-light'
                                }`}
                                style={{ width: '40px', height: '40px' }}
                              >
                                <FeatherIcon
                                  icon={item.icon}
                                  size={18}
                                  className={isActive ? 'text-white' : 'text-muted'}
                                />
                              </div>
                              <div className="text-start">
                                <div className="fw-semibold">{item.label}</div>
                                <small className={isActive ? 'text-white-50' : 'text-muted'}>
                                  {item.description}
                                </small>
                              </div>
                              {isActive && (
                                <div className="ms-auto">
                                  <FeatherIcon icon="check" size={18} className="text-white" />
                                </div>
                              )}
                            </motion.button>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                </CardBody>
              </Card>
            </motion.div>

            {/* Recipients — full-width below Status, technician-card list pattern */}
            <motion.div variants={cardVariants} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <SectionHeader
                    icon="users"
                    color="info"
                    title={t('compliance.materiovigilance.descending.field.recipients', 'Recipients')}
                    subtitle={t('compliance.materiovigilance.section.recipients_subtitle', 'People notified about this alert')}
                  />
                  {(record.recipientUsers && record.recipientUsers.length) ? (
                    <div className="users-grid">
                      {record.recipientUsers.map((u, i) => (
                        <motion.div
                          key={u.id}
                          className="user-card bg-light rounded p-3 mb-3"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <div
                                className="bg-info bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: '48px', height: '48px' }}
                              >
                                <FeatherIcon icon="user" size={20} className="text-info" />
                              </div>
                            </div>
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-semibold">{u.name}</h6>
                              {u.role && (
                                <Badge color="light" className="text-muted">
                                  {String(u.role).replace('ROLE_', '').replace(/_/g, ' ')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      icon="user-x"
                      title={t('compliance.materiovigilance.descending.field.recipients_empty', 'No recipients selected')}
                      description={t('compliance.materiovigilance.section.recipients_empty_desc', 'Add recipients while editing this alert.')}
                    />
                  )}
                </CardBody>
              </Card>
            </motion.div>
          </div>
        </TabPane>

        {/* ============== ASSETS — table pattern (sub-work-orders style) ============== */}
        <TabPane tabId="assets" className="h-100">
          <div className="p-4">
            <Row>
              <Col>
                <motion.div variants={cardVariants} initial="hidden" animate="visible">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="package"
                        color="success"
                        title={t('compliance.materiovigilance.descending.field.linked_assets', 'Assets')}
                        subtitle={t('compliance.materiovigilance.section.assets_subtitle', 'Equipment linked to this alert')}
                      />
                      {(record.linkedAssets && record.linkedAssets.length) ? (
                        <AssetListTable
                          data={record.linkedAssets}
                          excludeColumns={['location']}
                          onAssetClick={onAssetClick}
                          emptyMessage={t('compliance.materiovigilance.section.assets_empty', 'No Assets Linked')}
                          emptySubMessage={t('compliance.materiovigilance.section.assets_empty_desc', 'Equipment linked to this alert will appear here.')}
                        />
                      ) : (
                        <EmptyState
                          icon="package"
                          title={t('compliance.materiovigilance.section.assets_empty', 'No Assets Linked')}
                          description={t('compliance.materiovigilance.section.assets_empty_desc', 'Equipment linked to this alert will appear here.')}
                        />
                      )}
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </div>
        </TabPane>

        {/* ============== WORK ORDERS — same table pattern ============== */}
        <TabPane tabId="workorders" className="h-100">
          <div className="p-4">
            <Row>
              <Col>
                <motion.div variants={cardVariants} initial="hidden" animate="visible">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="briefcase"
                        color="primary"
                        title={t('compliance.materiovigilance.descending.field.linked_work_orders', 'Work Orders')}
                        subtitle={t('compliance.materiovigilance.section.work_orders_subtitle', 'Work orders linked to this alert')}
                      />
                      {(record.linkedWorkOrders && record.linkedWorkOrders.length) ? (
                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          <table className="table table-hover mb-0">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: '50px' }}>#</th>
                                <th>{t('shared.ticket', 'Ticket')}</th>
                                <th>{t('shared.asset', 'Asset')}</th>
                                <th>{t('shared.issue_type', 'Issue Type')}</th>
                                <th>{t('shared.status', 'Status')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {record.linkedWorkOrders.map((w, i) => (
                                <tr key={w.id}>
                                  <td className="fw-semibold">{i + 1}</td>
                                  <td>
                                    {onTicketClick && w.ticketNumber ? (
                                      <span
                                        className="ticket-link fw-semibold text-primary"
                                        onClick={() => onTicketClick(w.ticketNumber)}
                                        style={{ cursor: 'pointer' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                                      >
                                        {w.ticketNumber}
                                      </span>
                                    ) : (
                                      <span className="fw-medium">{w.ticketNumber || `#${w.id}`}</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <FeatherIcon icon="package" size={14} className="text-muted me-2" />
                                      <span>{w.assetName || t('shared.n_a', 'N/A')}</span>
                                    </div>
                                  </td>
                                  <td>
                                    {(w.issueType && w.issueType.length) ? (
                                      <div className="d-flex flex-wrap gap-1">
                                        {w.issueType.slice(0, 2).map((it, idx) => (
                                          <Badge key={idx} color="light" className="text-dark small">
                                            {String(it).replace(/_/g, ' ')}
                                          </Badge>
                                        ))}
                                        {w.issueType.length > 2 && (
                                          <Badge color="light" className="text-dark small">
                                            +{w.issueType.length - 2}
                                          </Badge>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-muted small">{t('shared.n_a', 'N/A')}</span>
                                    )}
                                  </td>
                                  <td>
                                    <Badge color={woStatusColor(w.status)} style={{ fontSize: '11px', padding: '4px 8px' }}>
                                      {woStatusLabel(w.status, t)}
                                    </Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <EmptyState
                          icon="briefcase"
                          title={t('compliance.materiovigilance.section.work_orders_empty', 'No Work Orders Linked')}
                          description={t('compliance.materiovigilance.section.work_orders_empty_desc', 'Work orders linked to this alert will appear here.')}
                        />
                      )}
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </div>
        </TabPane>

        {/* ============== ATTACHMENTS — document-item pattern ============== */}
        <TabPane tabId="attachments" className="h-100">
          <div className="p-4">
            <Row>
              <Col>
                <motion.div variants={cardVariants} initial="hidden" animate="visible">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="paperclip"
                        color="info"
                        title={t('compliance.materiovigilance.descending.field.attachments', 'Attachments')}
                        subtitle={t('compliance.materiovigilance.section.attachments_subtitle', 'Documents attached to this alert')}
                      />
                      {(record.attachments && record.attachments.length) ? (
                        <div className="documents-list">
                          {record.attachments.map((a, i) => (
                            <motion.a
                              key={a.id}
                              href={a.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="document-item d-flex align-items-center p-3 rounded bg-light text-decoration-none mb-2"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.05 }}
                            >
                              <div className="me-3">
                                <div
                                  className="bg-info bg-opacity-20 rounded d-flex align-items-center justify-content-center"
                                  style={{ width: '32px', height: '32px' }}
                                >
                                  <FeatherIcon icon="file-text" size={16} className="text-info" />
                                </div>
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="mb-0 text-dark">{a.label || a.fileName || t('shared.document', 'Document')}</h6>
                                <small className="text-muted">
                                  {a.uploadedAt
                                    ? `${formatCreatedDate(a.uploadedAt)} · ${t('shared.click_to_view', 'Click to view')}`
                                    : t('shared.click_to_view', 'Click to view')}
                                </small>
                              </div>
                              <FeatherIcon icon="external-link" size={16} className="text-muted" />
                            </motion.a>
                          ))}
                        </div>
                      ) : (
                        <EmptyState
                          icon="paperclip"
                          title={t('compliance.materiovigilance.section.attachments_empty', 'No Attachments')}
                          description={t('compliance.materiovigilance.section.attachments_empty_desc', 'Files attached to this alert will appear here.')}
                        />
                      )}
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </div>
        </TabPane>

        {/* ============== ACTIVITY — timeline pattern ============== */}
        <TabPane tabId="activity" className="h-100">
          <div className="p-4">
            <Row>
              <Col>
                <motion.div variants={cardVariants} initial="hidden" animate="visible">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-4">
                      <SectionHeader
                        icon="activity"
                        color="primary"
                        title={t('shared.activity_timeline', 'Activity Timeline')}
                        subtitle={t('compliance.materiovigilance.section.activity_subtitle', 'Audit trail for this alert')}
                      />
                      <div className="activity-timeline" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {(activity && activity.length) ? (
                          <div className="timeline">
                            {activity.map((entry, i) => {
                              const time = entry.at
                                ? new Date(entry.at).toLocaleTimeString(navigator.language || 'en-IN', { hour: '2-digit', minute: '2-digit' })
                                : '';
                              return (
                                <motion.div
                                  key={`${entry.kind}-${entry.id}`}
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: i * 0.05 }}
                                  className="timeline-item mb-4"
                                  style={{ position: 'relative', paddingLeft: '40px' }}
                                >
                                  {/* Timeline dot */}
                                  <div
                                    className="timeline-dot bg-primary"
                                    style={{
                                      position: 'absolute',
                                      left: '0',
                                      top: '8px',
                                      width: '12px',
                                      height: '12px',
                                      borderRadius: '50%',
                                      border: '3px solid #fff',
                                      boxShadow: '0 0 0 2px #0d6efd',
                                    }}
                                  />
                                  {/* Timeline line connector */}
                                  {i < activity.length - 1 && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        left: '5px',
                                        top: '20px',
                                        width: '2px',
                                        height: 'calc(100% + 16px)',
                                        backgroundColor: '#e9ecef',
                                      }}
                                    />
                                  )}
                                  {/* Activity card */}
                                  <div className="bg-light rounded p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <div className="d-flex align-items-center">
                                        <FeatherIcon icon="user" size={14} className="text-muted me-2" />
                                        <span className="fw-semibold text-dark">
                                          {entry.userName || t('shared.system', 'System')}
                                        </span>
                                      </div>
                                      <small className="text-muted">
                                        {entry.at && formatCreatedDate(entry.at)}
                                        {time && <span className="ms-1">{time}</span>}
                                      </small>
                                    </div>
                                    <div className="text-secondary small">
                                      {renderActivityPayload(entry, t, lookup)}
                                    </div>
                                  </div>
                                </motion.div>
                              );
                            })}
                          </div>
                        ) : (
                          <EmptyState
                            icon="message-square"
                            title={t('compliance.materiovigilance.section.activity_empty', 'No Activity Yet')}
                            description={t('compliance.materiovigilance.section.activity_empty_desc', 'Updates and changes will appear here over time.')}
                          />
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </div>
        </TabPane>
      </TabContent>
    </>
  );
};

const FieldRow = ({ label, required, children }) => (
  <Row className="mb-3">
    <Col md={12}>
      <label className="form-label fw-medium">
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      {children}
    </Col>
  </Row>
);

const ViewRow = ({ label, children }) => (
  <Row className="mb-3">
    <Col md={4} className="text-muted">{label}</Col>
    <Col md={8}>{children}</Col>
  </Row>
);

export default MaterioDescendingModal;
